import {Box, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import { BasicTableRowStyles, MainContentWrapper, TableHeaderWrapper, TableWrapper } from "../../components/common/Common.styles";
import ServerSideTableColumnHeader from "../../components/common/table/ServerSideTableColumnHeader";
import BorderRow from "../../components/common/table/BorderRow";
import CellBox from "../../components/common/table/CellBox";
import { useServerSidePagination } from "../../hooks/PaginationHooks";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store/store";
import { createNewNotificationThunk, deleteNotificationThunk, fetchInAppNotificationsThunk, updateNotificationThunk } from "../../store/thunk/inAppNotificationsThunk";
import { GenericInAppNotification, GenericInAppNotificationResponse } from "../../types/notificationType";
import { ServerSidePagination } from "../../components/common/table/ServerSidePagination";
import {Edit, InfoOutlined} from "@mui/icons-material";
import CreateOrEditGenericNotificationDialog from "./CreateOrEditGenericNotificationDialog";
import { useRef, useState } from "react";
import SearchBarButtonContainer from "../../components/common/table/SearchBarButtonContainer";
import PageTitle from "../../components/common/PageTitle/PageTitle";
import { useTranslation } from "react-i18next";
import GenericNotificationDetailDialog from "./GenericNotificationDetailDialog";
import { DatePicker } from "@mui/x-date-pickers";
import { Dayjs } from "dayjs";
import { GeneralButton } from "../../common/ui/GeneralButton";


export default function GenericInAppNotificationsPage() {
    const { t } = useTranslation();

    const [detailNotification, setDetailNotification] = useState<GenericInAppNotificationResponse | null>(null);

    const [createdFrom, setCreatedFrom] = useState<Dayjs | null>(null);
    const [createdTo, setCreatedTo] = useState<Dayjs | null>(null);

    const dispatch = useDispatch<AppDispatch>();

    const paginatedData = useServerSidePagination<GenericInAppNotificationResponse>(
        async (paginationParams) => {
            const result = await dispatch(fetchInAppNotificationsThunk({
                paginationParams,
                filters: {
                    createdFrom: createdFrom ? createdFrom.startOf("day").toISOString() : undefined,
                    createdTo: createdTo ? createdTo.endOf("day").toISOString() : undefined,
                },
            }));
            if (fetchInAppNotificationsThunk.fulfilled.match(result)) {
                return result.payload;
            }
            return { items: [], totalCount: 0 };
        },
        []
    );

    const filterDebounceRef = useRef<ReturnType<typeof setTimeout>>();

    const handleFilterChange = (change: { createdFrom?: Dayjs | null; createdTo?: Dayjs | null }) => {
        if (change.createdFrom !== undefined) setCreatedFrom(change.createdFrom);
        if (change.createdTo !== undefined) setCreatedTo(change.createdTo);
        clearTimeout(filterDebounceRef.current);
        filterDebounceRef.current = setTimeout(() => {
            paginatedData.updateTableItems();
        }, 500);
    };

    const resetFilters = () => {
        setCreatedFrom(null);
        setCreatedTo(null);
        clearTimeout(filterDebounceRef.current);
        filterDebounceRef.current = setTimeout(() => {
            paginatedData.updateTableItems();
        }, 500);
    };

    const [editingNotification, setEditingNotification] = useState<GenericInAppNotificationResponse>();
    const [newNotification, setNewNotification] = useState<boolean>();

    const saveEditedNotification = async (notification: GenericInAppNotification) => {
        await dispatch(updateNotificationThunk(notification));
        return notification.id;
    };

    const deleteEditedNotification = async (notification: GenericInAppNotification) => {
        await dispatch(deleteNotificationThunk(notification));
        paginatedData.updateTableItems();
        setEditingNotification(undefined);
    };

    const saveNewNotification = async (notification: GenericInAppNotification) => {
        const result = await dispatch(createNewNotificationThunk(notification));
        return createNewNotificationThunk.fulfilled.match(result) ? result.payload : undefined;
    };

    return <>
        <PageTitle
            breadcrumbs={[]}
            title={t("genericInAppNotificationsPage.title")}
        />
        <MainContentWrapper>
            <Box style={{ padding: "2em", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1em" }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}>
                    <DatePicker
                        label={t("genericInAppNotificationsPage.filters.createdFrom")}
                        value={createdFrom}
                        onChange={(value) => handleFilterChange({ createdFrom: value as Dayjs | null })}
                        maxDate={createdTo ?? undefined}
                        slotProps={{ field: { clearable: true }, textField: { size: "small" } }}
                    />
                    <DatePicker
                        label={t("genericInAppNotificationsPage.filters.createdTo")}
                        value={createdTo}
                        onChange={(value) => handleFilterChange({ createdTo: value as Dayjs | null })}
                        minDate={createdFrom ?? undefined}
                        slotProps={{ field: { clearable: true }, textField: { size: "small" } }}
                    />
                    {(createdFrom || createdTo) && (
                        <GeneralButton appVariant="ghost" onClick={resetFilters}>
                            {t("buttons.reset")}
                        </GeneralButton>
                    )}
                </Stack>
                <SearchBarButtonContainer
                    onCreateClick={() => setNewNotification(true)}
                />
            </Box>
            <TableWrapper
                columnsTemplate="1fr 2fr 7rem max-content"
                sx={{ backgroundColor: theme => theme.palette.background.paper }}
            >
                <TableHeaderWrapper>
                    <ServerSideTableColumnHeader
                        isSorted={false}
                        label={t("genericInAppNotificationsPage.columns.title")}
                        firstCol
                        paginatedData={paginatedData}
                    />
                    <ServerSideTableColumnHeader
                        isSorted={false}
                        label={t("genericInAppNotificationsPage.columns.message")}
                        paginatedData={paginatedData}
                    />
                    <ServerSideTableColumnHeader
                        isSorted={false}
                        label={t("genericInAppNotificationsPage.columns.readCount")}
                        paginatedData={paginatedData}
                    />
                    <ServerSideTableColumnHeader
                        isSorted={false}
                        label=""
                        paginatedData={paginatedData}
                        lastCol
                    />
                    <BorderRow />
                </TableHeaderWrapper>
                <Box sx={{ display: "contents" }}>
                    {
                        paginatedData.items.map(n =>
                            <BasicTableRowStyles onClick={() => { }}>
                                <CellBox firstCol stickyFirstCol>
                                    <Typography>{n.title}</Typography>
                                </CellBox>
                                <CellBox>
                                    <Typography>{n.message}</Typography>
                                </CellBox>
                                <CellBox>
                                    <Typography>{n.readCount} / {n.totalCount}</Typography>
                                    {n.readCount > 0 && (
                                        <Tooltip title={t("genericInAppNotificationsPage.details")}>
                                            <IconButton size="small" onClick={() => setDetailNotification(n)}>
                                                <InfoOutlined fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </CellBox>
                                <CellBox lastCol>
                                    <IconButton onClick={() => setEditingNotification(n)}>
                                        <Edit />
                                    </IconButton>
                                </CellBox>
                            </BasicTableRowStyles>
                        )
                    }
                </Box>
            </TableWrapper>
            <ServerSidePagination paginatedData={paginatedData} />

            {detailNotification && (
                <GenericNotificationDetailDialog
                    notification={detailNotification}
                    onClose={() => setDetailNotification(null)}
                />
            )}
            {editingNotification && <CreateOrEditGenericNotificationDialog
                onSave={saveEditedNotification}
                onDelete={deleteEditedNotification}
                onClose={() => setEditingNotification(undefined)}
                onRefresh={() => paginatedData.updateTableItems()}
                editingNotification={editingNotification!}
            />}

            {newNotification && <CreateOrEditGenericNotificationDialog
                onSave={saveNewNotification}
                onClose={() => setNewNotification(false)}
                onRefresh={() => paginatedData.updateTableItems()}
            />}
        </MainContentWrapper>
    </>
}